import React, { Fragment, PureComponent } from 'react';
import LearnosityProvider from 'legacy-learnosity-player/src/utility/LearnosityProvider';
import { BOOKMARKED_QUESTIONS } from 'legacy/src/constants/common';
import SkinWrapperDesktop from './SkinWrapperDesktop';
import AnnotationContainer from './Annotation/MobileContainer';
import DomWrapper from '../utility/DomWrapper';
import QuestionsOverview from './QuestionsOverview';
import QuestionInfo from './QuestionInfo';
import MobileToolkit from './MobileToolkit';
import MobileItemTabs from './MobileItemTabs';
import MobileTimer from './MobileTimer';
import QuestionList from './QuestionList';
import DevicesContext from '../utility/DevicesContext';
import LearnosityContext from 'legacy-learnosity-player/src/utility/LearnosityContext';
import { Storage } from '../utility/Storage';
import { forEach } from 'lodash';
import Sticky from 'legacy-learnosity-player/src/utility/Sticky';
import CharmBracelet from './CharmBracelet';
import BottomStickyContainer from './BottomStickyContainer';
import { connect } from 'react-redux';

import './Player.scss';
import SaveExitButton from './SaveExitButton';
import {
  setBookmarks,
  addBookmark,
  removeBookmark,
} from 'legacy-src/actions/bookmarks';
import CheckAnswerButton from './CheckAnswerButton';

/**
 * Wrapper consumer propagates the props from each consumer
 * to children as props
 * */
const ConsumerWrapper = ({ children, ...props }) =>
  !!children.length
    ? React.Children.toArray(children).map(
        (child, index) =>
          child &&
          React.cloneElement(child, {
            ...props,
            key: `consumer-child-${index}`,
          }),
      )
    : children && React.cloneElement(children, { ...props });

/**
 * Draws the player
 * */
class Player extends PureComponent {
  constructor() {
    super();
    this.state = {
      lastQuestionRef: null,
      showQuestionList: false,
    };
    this.observer = null;
  }

  componentDidMount() {
    const { isImpersonating, preview, setBookmarks } = this.props;
    const { session_id } = this.props.learnosityServerData.request;
    this.bookmarkStore = new Storage(
      `${BOOKMARKED_QUESTIONS}-${session_id}${isImpersonating ? '-i' : ''}`,
    );
    setBookmarks(this.bookmarkStore.getQuestions());

    // ensure math pallete widget is added to focus lock group when it's added to the dom
    const focusGroup = preview
      ? 'part-of-screen-modal'
      : 'student-player-group';
    this.observer = new MutationObserver((mutations_list) => {
      mutations_list.forEach((mutation) => {
        mutation.addedNodes.forEach((added_node) => {
          if (
            added_node.classList &&
            Array.from(added_node.classList).includes(
              'lrn_charactermap_wrapper',
            )
          ) {
            added_node.classList.add('focus-lock');
            added_node.setAttribute('data-focus-lock', focusGroup);
          }
        });
      });
    });

    this.observer.observe(document.querySelector('body'), {
      subtree: false,
      childList: true,
    });
  }

  componentWillUnmount() {
    this.observer && this.observer.disconnect();
  }

  onHighlight(reference) {
    const refTermArray = document.querySelectorAll(
      `[data-reference="${reference}"] .row .lrn_sharedpassage .referenced_term`,
    );
    const HIGHLIGHT_ACTIVE = 'highlight';

    const nodeList = document.querySelectorAll(
      `.referenced_term.${HIGHLIGHT_ACTIVE}`,
    );
    forEach(nodeList, (i) => i.classList.remove(HIGHLIGHT_ACTIVE));

    forEach(refTermArray, (term) => {
      const list = term.classList;
      if (list.contains(`itemref:${reference}`)) {
        list.add(HIGHLIGHT_ACTIVE);
      }
    });
  }

  onTabChanged = (visibleTab) => {
    this.onTabChange && this.onTabChange(visibleTab);
  };

  questionContainerSelector = (questionReference) =>
    `.learnosity-item[data-reference="${questionReference}"]`;

  componentWillReceiveProps(nextProps) {
    const itemCounter = nextProps.itemCounter;
    if (this.props.itemCounter !== itemCounter) {
      itemCounter.addEventListener('click', () => {
        this.setState({
          showQuestionList: true,
        });
      });
    }
  }

  render() {
    const {
      onSubmitAssignmentSuccess,
      onSubmitAssignmentError,
      apiType,
      endpoint,
      containerElement,
      learnosityServerData,
      preview,
      children,
      allowCalculatorForItems,
      annotationData,
      commonProps,
      readyListener,
      isPreApSubject,
      responsiveView: ResponsiveView,
      navigation,
      showLargeFont,
      showQuestionInfo,
      showQuestionOverview,
      showCalculatorAllowedIcon,
      showQuestionIndex,
      showQuestionBookmark,
      pageTitle,
      isDummyPlayer,
      title,
      timerInfo,
      isSoftTime,
      onShowModal,
      onOrderChange,
      isReordering,
      timer,
      checkTimeExtension,
      checkTimeExtensionInterval,
      onTimerExpiring,
      toogleStatus,
      showUpdatedCharmBracelet,
      isMetaDataPanelExpanded,
      showGrayTopRightBarPreview,
      questionChanged,
      formulaSheet,
      periodicTable,
      showDoubleArrow,
      isImpersonating,
      onItemChanged,
      bookmarks,
      addBookmark,
      removeBookmark,
      playerContainerId,
    } = this.props;

    const timerProps = {
      timerInfo,
      isSoftTime,
      onShowModal,
      timer,
      checkTimeExtension,
      checkTimeExtensionInterval,
      onTimerExpiring,
    };

    const { showQuestionList } = this.state;

    return (
      <div className="PlayerModeContainer--content">
        <LearnosityProvider
          key="legacy-learnosity-player-1"
          apiType={apiType}
          endpoint={endpoint}
          containerElement={containerElement}
          initOptions={learnosityServerData}
          preview={preview}
          readyListener={readyListener}
        >
          <DevicesContext.Consumer>
            {(devicesData) => (
              <LearnosityContext.Consumer>
                {(learnosityData) => {
                  if (!learnosityData) {
                    return null;
                  }

                  return (
                    <Fragment>
                      <ConsumerWrapper
                        {...commonProps}
                        playerContainerId={playerContainerId}
                        devicesData={devicesData}
                        learnosityData={learnosityData}
                        onHighlight={this.onHighlight}
                      >
                        <ResponsiveView screens={['desktop']}>
                          <Sticky learnosityData={learnosityData} />
                          <SkinWrapperDesktop
                            preview={preview}
                            onSubmitAssignmentSuccess={
                              onSubmitAssignmentSuccess
                            }
                            onSubmitAssignmentError={onSubmitAssignmentError}
                            annotationData={annotationData}
                            allowCalculatorForItems={allowCalculatorForItems}
                            showCalculatorAllowedIcon={
                              showCalculatorAllowedIcon
                            }
                            showLargeFont={showLargeFont}
                            isPreApSubject={isPreApSubject}
                            isDummyPlayer={isDummyPlayer}
                            toogleStatus={toogleStatus}
                            isMetaDataPanelExpanded={isMetaDataPanelExpanded}
                            showGrayTopRightBarPreview={
                              showGrayTopRightBarPreview
                            }
                            questionChanged={questionChanged}
                            isMobile={false}
                            formulaSheet={formulaSheet}
                            periodicTable={periodicTable}
                            isImpersonating={isImpersonating}
                            showQuestionBookmark={showQuestionBookmark}
                          />
                          {navigation && (
                            <DomWrapper
                              containerSelector={
                                this.props.navigationContainerSelector
                                  ? this.props.navigationContainerSelector
                                  : '.lrn-group-middle'
                              }
                              nodeClass="custom-question-navigation"
                            >
                              {navigation}
                            </DomWrapper>
                          )}
                          {this.props.assignment?.hasImmediateFeedback &&
                            this.props.immediateFeedbackFF && (
                              <DomWrapper
                                containerSelector=".nav-container"
                                nodeClass="check-answer"
                              >
                                <CheckAnswerButton
                                  isImpersonating={isImpersonating}
                                  immediateFeedbackAttempts={
                                    this.props.assignment
                                      ?.immediateFeedbackAttempts
                                  }
                                />
                              </DomWrapper>
                            )}
                          {showQuestionOverview && (
                            <DomWrapper
                              containerSelector=".top-right-wrapper"
                              nodeClass="top-questions-overview"
                              isReordering={isReordering}
                              isReady={commonProps.isLearnosityReady}
                              isMetaDataPanelExpanded={
                                showGrayTopRightBarPreview &&
                                isMetaDataPanelExpanded
                              }
                              updatableProps={[
                                'isReordering',
                                'isMetaDataPanelExpanded',
                                'isReady',
                              ]}
                            >
                              {showUpdatedCharmBracelet ? (
                                <CharmBracelet
                                  onOrderChange={onOrderChange}
                                  onItemChanged={onItemChanged}
                                  key="QuestionsOverview-desktop"
                                  preview={preview}
                                  showDoubleArrow={showDoubleArrow}
                                  isImpersonating={isImpersonating}
                                />
                              ) : (
                                <QuestionsOverview
                                  key="QuestionsOverview-desktop"
                                  preview={preview}
                                  isImpersonating={isImpersonating}
                                />
                              )}
                            </DomWrapper>
                          )}
                          {showQuestionInfo && (
                            <DomWrapper
                              containerSelector=".lrn-items-region"
                              nodeClass="question-info"
                              tabIndex="0"
                            >
                              <QuestionInfo
                                allowCalculatorForItems={
                                  allowCalculatorForItems
                                }
                                showCalculatorAllowedIcon={
                                  showCalculatorAllowedIcon
                                }
                                showQuestionBookmark={showQuestionBookmark}
                                showQuestionIndex={showQuestionIndex}
                                isImpersonating={isImpersonating}
                              />
                            </DomWrapper>
                          )}
                        </ResponsiveView>
                        <ResponsiveView screens={['mobile']}>
                          {showQuestionList && (
                            <QuestionList
                              showUpdatedCharmBracelet={
                                showUpdatedCharmBracelet
                              }
                              title={pageTitle || title}
                              preview={preview}
                              onClose={() =>
                                this.setState({ showQuestionList: false })
                              }
                            />
                          )}
                          {!preview && <BottomStickyContainer />}
                          {this.props.assignment?.hasImmediateFeedback &&
                            this.props.immediateFeedbackFF && (
                              <DomWrapper
                                containerSelector=".nav-container"
                                nodeClass="check-answer"
                              >
                                <CheckAnswerButton
                                  isImpersonating={isImpersonating}
                                  immediateFeedbackAttempts={
                                    this.props.assignment
                                      ?.immediateFeedbackAttempts
                                  }
                                />
                              </DomWrapper>
                            )}
                          <DomWrapper
                            containerSelector=".top-right-wrapper"
                            nodeClass="save-exit-container"
                          >
                            <SaveExitButton
                              onShowModal={onShowModal}
                              isMobile
                            />
                          </DomWrapper>
                          <DomWrapper
                            containerSelector=".slides-container"
                            nodeClass="mobile-tabs"
                          >
                            <MobileItemTabs onChange={this.onTabChanged}>
                              {showQuestionInfo && (
                                <DomWrapper
                                  containerSelector=".lrn-items-region"
                                  nodeClass="question-info"
                                >
                                  <QuestionInfo
                                    allowCalculatorForItems={
                                      allowCalculatorForItems
                                    }
                                    showQuestionBookmark={false}
                                  />
                                </DomWrapper>
                              )}
                            </MobileItemTabs>
                          </DomWrapper>
                          {timerInfo && timerInfo.totalTime && (
                            <DomWrapper
                              containerSelector=".top-right-wrapper"
                              nodeClass="mobile-timer"
                            >
                              <MobileTimer {...timerProps} />
                            </DomWrapper>
                          )}
                          <DomWrapper
                            containerSelector="#learnosity_assess"
                            nodeClass="mobile-toolkit-wrapper"
                            tabIndex="0"
                            aria-label="Toolkit Container"
                            questionChanged={questionChanged}
                            updatableProps={['questionChanged', 'bookmarks']}
                            bookmarks={bookmarks}
                          >
                            <AnnotationContainer
                              annotationData={annotationData}
                              parentComponent={this}
                              isImpersonating={isImpersonating}
                            >
                              <MobileToolkit
                                isPreApSubject={isPreApSubject}
                                allowCalculatorForItems={
                                  allowCalculatorForItems
                                }
                                showCalculatorAllowedIcon={
                                  showCalculatorAllowedIcon
                                }
                                questionChanged={questionChanged}
                                formulaSheet={formulaSheet}
                                periodicTable={periodicTable}
                                isImpersonating={isImpersonating}
                                showQuestionBookmark={showQuestionBookmark}
                                bookmarks={bookmarks}
                                addBookmark={addBookmark}
                                removeBookmark={removeBookmark}
                              />
                            </AnnotationContainer>
                          </DomWrapper>
                        </ResponsiveView>
                        {children}
                      </ConsumerWrapper>
                    </Fragment>
                  );
                }}
              </LearnosityContext.Consumer>
            )}
          </DevicesContext.Consumer>
        </LearnosityProvider>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  const { bookmarks } = state;

  return {
    bookmarks,
  };
};

const mapDispatchToProps = {
  setBookmarks,
  addBookmark,
  removeBookmark,
};

export default connect(mapStateToProps, mapDispatchToProps)(Player);
